import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  Option,
  Select,
  Stack,
  Typography,
  Checkbox,
} from '@mui/joy';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { BillingRecordPicker } from './BillingRecordPicker';

const money = (n: any) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));
const failure = (e: any) =>
  e.responseData?.detail ||
  e.response?.data?.detail ||
  e.message ||
  'Unable to save. Please retry.';
export function SavedBillingViews({ kind }: { kind: string }) {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const key = `billing-views:${user?.organization}:${user?.id}:${kind}`;
  const [views, setViews] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(key) || '{}');
    } catch {
      return {};
    }
  });
  const [name, setName] = useState('');
  return (
    <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mb: 2 }}>
      <Select
        placeholder="Saved views"
        aria-label="Saved billing view"
        onChange={(_, value) => value && setParams(new URLSearchParams(views[String(value)]))}
      >
        {Object.keys(views).map((n) => (
          <Option key={n} value={n}>
            {n}
          </Option>
        ))}
      </Select>
      <Input
        aria-label="View name"
        placeholder="Name this view"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Button
        variant="outlined"
        disabled={!name.trim()}
        onClick={() => {
          const next = { ...views, [name.trim()]: params.toString() };
          localStorage.setItem(key, JSON.stringify(next));
          setViews(next);
          setName('');
        }}
      >
        Save view
      </Button>
    </Stack>
  );
}
export function BillingQueues() {
  return (
    <Card sx={{ mb: 3 }}>
      <Typography level="title-lg">Billing work queue</Typography>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
        <Button component={Link} to="/operations/billing/estimates?status=sent" variant="soft">
          Awaiting acceptance
        </Button>
        <Button component={Link} to="/operations/billing/invoices?queue=due_soon" variant="soft">
          Due in 7 days
        </Button>
        <Button
          component={Link}
          to="/operations/billing/invoices?queue=overdue"
          variant="soft"
          color="danger"
        >
          Overdue balances
        </Button>
        <Button component={Link} to="/operations/billing/invoices?queue=unassigned" variant="soft">
          Invoices without a job
        </Button>
        <Button
          component={Link}
          to="/operations/billing/payments?is_reconciled=false"
          variant="soft"
        >
          Unreconciled payments
        </Button>
      </Stack>
      <InstallmentBuilder />
    </Card>
  );
}
export function InstallmentBuilder() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('job');
  const [source, setSource] = useState<number | null>(null);
  const [kind, setKind] = useState('deposit');
  const [amount, setAmount] = useState('');
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['installment-preview', type, source],
    enabled: open && !!source,
    queryFn: async () =>
      (
        await apiClient.get<any>(
          `/api/invoices/installments/?source_type=${type}&source_id=${source}`,
        )
      ).data,
  });
  const create = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<any>('/api/invoices/installments/', {
          source_type: type,
          source_id: source,
          kind,
          amount: kind === 'final' ? query.data.remaining : amount,
          expected_remaining: query.data.remaining,
        })
      ).data,
    onSuccess: (invoice) => navigate(`/operations/billing/invoices/${invoice.id}`),
    onError: () => query.refetch(),
  });
  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Create deposit / progress / final invoice
      </Button>
      <Modal open={open} onClose={() => !create.isPending && setOpen(false)}>
        <ModalDialog sx={{ width: 620, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Review installment</Typography>
          <Select
            aria-label="Invoice source"
            value={type}
            onChange={(_, v) => {
              setType(v || 'job');
              setSource(null);
            }}
          >
            <Option value="job">Job value</Option>
            <Option value="estimate">Estimate</Option>
          </Select>
          <BillingRecordPicker
            label={type === 'job' ? 'Job' : 'Estimate'}
            endpoint={type === 'job' ? '/api/jobs/?include_completed=true' : '/api/estimates/'}
            value={source}
            onChange={(row) => setSource(row?.id || null)}
          />
          {query.isError && (
            <Alert color="danger">
              Unable to load source. <Button onClick={() => query.refetch()}>Retry</Button>
            </Alert>
          )}
          {query.data && (
            <>
              <Typography>{query.data.customer_name}</Typography>
              <Typography>
                Source: {money(query.data.source_total)} · Already invoiced:{' '}
                {money(query.data.already_invoiced)} · Remaining: {money(query.data.remaining)}
              </Typography>
              <Select
                aria-label="Installment kind"
                value={kind}
                onChange={(_, v) => setKind(v || 'deposit')}
              >
                <Option value="deposit">Deposit</Option>
                <Option value="progress">Progress</Option>
                <Option value="final">Final balance</Option>
              </Select>
              <FormControl>
                <FormLabel>Installment amount</FormLabel>
                <Input
                  type="number"
                  slotProps={{ input: { min: 0, step: 0.01 } }}
                  value={kind === 'final' ? query.data.remaining : amount}
                  disabled={kind === 'final'}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </FormControl>
              <Alert>
                Creates a draft invoice for this portion of the source total. No additional tax is
                added automatically; review the draft's tax treatment before sending.
              </Alert>
              <Typography level="body-sm">
                Remaining after this invoice:{' '}
                {money(
                  Number(query.data.remaining) -
                    Number(kind === 'final' ? query.data.remaining : amount),
                )}
              </Typography>
              <Button
                loading={create.isPending}
                disabled={
                  Number(query.data.remaining) <= 0 ||
                  (kind !== 'final' &&
                    (!Number(amount) || Number(amount) > Number(query.data.remaining)))
                }
                onClick={() => create.mutate()}
              >
                Create reviewed draft
              </Button>
            </>
          )}
          {create.isError && <Alert color="danger">{failure(create.error)}</Alert>}
        </ModalDialog>
      </Modal>
    </>
  );
}
export function PaymentReconciliation({ payment }: { payment: any }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (checked: boolean) =>
      apiClient.patch(`/api/payments/${payment.id}/`, { is_reconciled: checked }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payments'] }),
  });
  return (
    <Box>
      <Typography level="body-sm">
        Gross {money(payment.amount)} · Refunded {money(payment.refunded_amount)} · Net{' '}
        {money(payment.net_amount)}
      </Typography>
      <Typography level="body-xs">Invoice balance {money(payment.invoice_balance)}</Typography>
      <Checkbox
        label="Reconciled"
        checked={!!payment.is_reconciled}
        disabled={mutation.isPending}
        onChange={(e) => mutation.mutate(e.target.checked)}
      />
      {mutation.isError && <Typography color="danger">{failure(mutation.error)}</Typography>}
    </Box>
  );
}
export function RecurringControl({ templateId }: { templateId: number }) {
  const [page, setPage] = useState(1);
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['recurring-control', templateId, page],
    queryFn: async () =>
      (await apiClient.get<any>(`/api/recurring-templates/${templateId}/control/?page=${page}`))
        .data,
  });
  const mutation = useMutation({
    mutationFn: ({ id, command }: { id: number; command: string }) =>
      apiClient.post(`/api/recurring-templates/${templateId}/control/`, {
        schedule_id: id,
        command,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-control', templateId] });
      qc.invalidateQueries({ queryKey: ['recurring-templates'] });
    },
  });
  return (
    <Card sx={{ my: 2 }}>
      <Typography level="title-lg">Upcoming invoices and history</Typography>
      {query.isLoading && <Typography>Loading schedule…</Typography>}
      {query.isError && <Button onClick={() => query.refetch()}>Retry schedule</Button>}
      {mutation.isError && <Alert color="danger">{failure(mutation.error)}</Alert>}
      {query.data?.upcoming.map((s: any) => (
        <Stack key={s.id} direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography>
            {s.scheduled_for} · {money(query.data.amount)}
          </Typography>
          {s.requires_approval && !s.is_approved ? (
            <Button
              size="sm"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ id: s.id, command: 'approve' })}
            >
              Approve occurrence
            </Button>
          ) : (
            <Typography level="body-sm">Approved</Typography>
          )}
          <Button
            size="sm"
            variant="outlined"
            disabled={mutation.isPending || new Date(s.scheduled_for) > new Date()}
            onClick={() => mutation.mutate({ id: s.id, command: 'retry' })}
          >
            Generate / retry
          </Button>
        </Stack>
      ))}
      {query.data?.history.map((row: any) => (
        <Box key={row.id}>
          <Typography>
            {row.scheduled_date} · {row.status} · {money(row.calculated_amount)}
          </Typography>
          {row.invoice_id && (
            <Button
              component={Link}
              to={`/operations/billing/invoices/${row.invoice_id}`}
              variant="plain"
              size="sm"
            >
              Open invoice
            </Button>
          )}
          {row.error_message && <Typography color="danger">{row.error_message}</Typography>}
        </Box>
      ))}
      <Stack direction="row" spacing={1}>
        <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Newer
        </Button>
        <Typography>Page {page}</Typography>
        <Button
          disabled={!query.data || page * 20 >= query.data.history_count}
          onClick={() => setPage((p) => p + 1)}
        >
          Older
        </Button>
      </Stack>
    </Card>
  );
}
export function BillingDefaultsPreview({ values, defaults }: { values: any; defaults: any }) {
  const [kind, setKind] = useState('invoice');
  const org = useQuery({
    queryKey: ['billing-preview-org'],
    queryFn: async () => (await apiClient.get<any>('/api/organizations/current/')).data,
  });
  const tokens: Record<string, string> = {
    customer_name: 'Jordan Sample',
    customer_first_name: 'Jordan',
    document_number: kind === 'estimate' ? 'EST-SAMPLE' : 'INV-SAMPLE',
    document_total: '$1,000.00',
    balance_due: '$1,000.00',
    company_name: org.data?.name || 'Your company',
    company_phone: org.data?.company_phone_number || '',
    payment_url: values.payment_url || '',
  };
  const fill = (text: string) =>
    text.replace(/\{([^{}]+)\}/g, (match, key) => tokens[key] ?? match);
  const emailBody = fill(values[`${kind}_email_body`] || 'Dear {customer_first_name},\nPlease find your document attached.\n{company_name}');
  const paymentFooter = kind === 'invoice' && tokens.payment_url && !emailBody.includes(tokens.payment_url) ? `\n\nPay online: ${tokens.payment_url}` : '';
  return (
    <Card sx={{ my: 3 }}>
      <Typography level="title-lg">Preview billing defaults</Typography>
      <Select
        aria-label="Preview document"
        value={kind}
        onChange={(_, v) => setKind(v || 'invoice')}
      >
        <Option value="estimate">Estimate</Option>
        <Option value="invoice">Invoice</Option>
        <Option value="receipt">Receipt</Option>
      </Select>
      <Box sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
        {org.data?.logo_url && (
          <Box
            component="img"
            src={org.data.logo_url}
            alt="Company logo"
            sx={{ maxWidth: 160, maxHeight: 64, objectFit: 'contain', mb: 1 }}
          />
        )}
        <Typography level="h4">{tokens.company_name}</Typography>
        <Typography level="title-lg">{kind.toUpperCase()} · SAMPLE</Typography>
        <Typography>Jordan Sample · $1,000.00</Typography>
        {kind !== 'receipt' && (
          <>
            <Typography>
              Payment terms:{' '}
              {(defaults[kind]?.default_payment_terms || 'net_30').replaceAll('_', ' ')}
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{defaults[kind]?.default_notes}</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>
              {defaults[kind]?.terms_and_conditions}
            </Typography>
          </>
        )}
        {kind === 'invoice' && values.payment_url && (
          <Typography>Pay online: {values.payment_url}</Typography>
        )}
      </Box>
      <Typography level="title-sm">Email subject</Typography>
      <Typography>
        {fill(
          values[`${kind}_email_subject`] ||
            `${kind === 'receipt' ? 'Payment receipt' : 'Your ' + kind} from {company_name}`,
        )}
      </Typography>
      <Typography sx={{ whiteSpace: 'pre-wrap' }}>
        {emailBody + paymentFooter}
      </Typography>
      <Typography level="body-sm">
        Defaults apply to new documents. Existing document notes and terms take precedence. This
        sample shows wording and payment instructions; use a document PDF to review the final
        layout.
      </Typography>
    </Card>
  );
}
